// Owner: interaction-kit (wave 1), design-system (wave 2). The generic glob-discovered registry behind
// every client extension point of PLAN §4.8: hosts render contributions, contributors add a file in their
// own feature folder, nobody edits a registry. Contracts: docs/CONTRACTS.md (interaction-kit).

import { devPagesEnabled } from './dev-pages';

/** A contribution with the file it came from (the dev listing shows both). */
export type Contributed<T> = { value: T; source: string };

export type ExtensionRegistry<T> = {
  /** Extension point id (`record-components`, `bulk-actions`, …). */
  point: string;
  /** Glob of contribution files, relative to `apps/web/src`. */
  glob: string;
  /** Workstream that hosts the point. */
  host: string;
  list(): T[];
  entries(): Contributed<T>[];
  get(key: string): T | undefined;
  /** Contribution files that failed validation (shown by the dev listing; thrown in development). */
  problems(): Array<{ source: string; message: string }>;
};

const PROBE = '/_probe/';

/**
 * Build a registry from an eager `import.meta.glob` result. Each file default-exports one contribution
 * or an array. Keys must be unique; a duplicate or a malformed export throws in development and is
 * skipped (with a console warning) in production. Files under `features/_probe/` (the kit's discovery
 * probe) are only loaded in development.
 *
 * The contributions are read on first use, not when the registry module loads: a contribution file
 * that imports a helper from its point's module (or a screen that imports a contribution file before
 * the registry) is then a harmless import cycle instead of a file read before its default export exists.
 */
export function createExtensionRegistry<T>(options: {
  point: string;
  glob: string;
  host: string;
  modules: Record<string, unknown>;
  key: (value: T) => string;
  check: (value: unknown) => value is T;
  dev?: boolean;
}): ExtensionRegistry<T> {
  // Strict in development; the probe also loads in builds that ship the developer pages (e2e gate).
  const dev = options.dev ?? import.meta.env.DEV;
  const probes = options.dev ?? devPagesEnabled;
  let built: {
    items: Contributed<T>[];
    problems: Array<{ source: string; message: string }>;
    byKey: Map<string, Contributed<T>>;
  } | null = null;
  const build = () => built ?? (built = collect());
  const collect = () => {
    const items: Contributed<T>[] = [];
    const problems: Array<{ source: string; message: string }> = [];
    const byKey = new Map<string, Contributed<T>>();
    for (const [path, mod] of Object.entries(options.modules).sort(([a], [b]) => a.localeCompare(b))) {
      const source = path.replace(/^(\.\.\/)+/, 'src/');
      if (!probes && path.includes(PROBE)) continue;
      const exported = (mod as { default?: unknown } | undefined)?.default;
      const values = Array.isArray(exported) ? exported : exported === undefined ? [] : [exported];
      if (exported === undefined) problems.push({ source, message: 'has no default export' });
      for (const value of values) {
        if (!options.check(value)) {
          problems.push({ source, message: `exports a value that is not a ${options.point} contribution` });
          continue;
        }
        const key = options.key(value);
        const clash = byKey.get(key);
        if (clash) {
          problems.push({ source, message: `"${key}" is already contributed by ${clash.source}` });
          continue;
        }
        const entry = { value, source };
        byKey.set(key, entry);
        items.push(entry);
      }
    }
    if (problems.length) {
      const text = problems.map((p) => `${options.point}: ${p.source} ${p.message}`).join('\n');
      if (dev) throw new Error(text);
      console.warn(text);
    }
    return { items, problems, byKey };
  };
  return {
    point: options.point,
    glob: options.glob,
    host: options.host,
    list: () => build().items.map((i) => i.value),
    entries: () => [...build().items],
    get: (key) => build().byKey.get(key)?.value,
    problems: () => [...build().problems],
  };
}

/** The property `name` of a contribution (undefined when the value is no object or lacks it). */
export function memberOf(value: unknown, name: string): unknown {
  return typeof value === 'object' && value !== null ? Reflect.get(value, name) : undefined;
}
/** Whether a contribution has a property `name` that is set (a component, a schema). */
export const hasMember = (value: unknown, name: string): boolean => Boolean(memberOf(value, name));
/** Whether a contribution has a function property `name`. */
export const hasFunction = (value: unknown, name: string): boolean =>
  typeof memberOf(value, name) === 'function';

/** Shape check helper: an object with string properties `keys` and function properties `fns`. */
export function hasShape(value: unknown, keys: readonly string[], fns: readonly string[] = []): boolean {
  if (typeof value !== 'object' || value === null) return false;
  return keys.every((k) => typeof memberOf(value, k) === 'string') && fns.every((f) => hasFunction(value, f));
}
